<script setup>
/**
 * Determinate and indeterminate progress.
 *
 * The filled width is a runtime percentage, which has no Tailwind-utility
 * expression (a dynamic arbitrary value like `w-[43%]` is not statically
 * scannable, so it would not be generated). It is therefore bound with `style`,
 * matching the existing precedent in
 * `routes/dashboard/settings/data/components/ImportProgress.vue`. This is the
 * only inline style in the component.
 */
import { computed } from 'vue';
import { useI18n } from 'vue-i18n';

const props = defineProps({
  value: {
    type: Number,
    default: 0,
  },
  max: {
    type: Number,
    default: 100,
  },
  /** Progress is happening but its extent is unknown. Ignores `value`. */
  indeterminate: {
    type: Boolean,
    default: false,
  },
  /** Visible label above the bar. */
  label: {
    type: String,
    default: '',
  },
  /** Show the percentage (or `value of max`) beside the label. */
  showValue: {
    type: Boolean,
    default: true,
  },
  /** Render `value of max` rather than a percentage. */
  showAsCount: {
    type: Boolean,
    default: false,
  },
  size: {
    type: String,
    default: 'md',
    validator: value => ['sm', 'md'].includes(value),
  },
  color: {
    type: String,
    default: 'violet',
    validator: value =>
      ['violet', 'blue', 'teal', 'amber', 'ruby'].includes(value),
  },
});

const { t } = useI18n();

const SIZE_CLASSES = { sm: 'h-1', md: 'h-1.5' };
const COLOR_CLASSES = {
  violet: 'bg-n-brand-violet',
  blue: 'bg-n-blue-9',
  teal: 'bg-n-teal-9',
  amber: 'bg-n-amber-9',
  ruby: 'bg-n-ruby-9',
};

const percent = computed(() => {
  if (props.max <= 0) return 0;
  return Math.min(
    100,
    Math.max(0, Math.round((props.value / props.max) * 100))
  );
});

/**
 * The accessible text equivalent. Always rendered - visibly when `showValue` is
 * set, otherwise visually hidden - so the bar is never colour-only.
 */
const valueText = computed(() => {
  if (props.indeterminate) return t('PROGRESS_BAR.INDETERMINATE');
  if (props.showAsCount) {
    return t('PROGRESS_BAR.OF_TOTAL', { value: props.value, total: props.max });
  }

  return t('PROGRESS_BAR.PERCENT_COMPLETE', { percent: percent.value });
});
</script>

<template>
  <div class="flex flex-col gap-1.5">
    <div
      v-if="label || showValue"
      class="flex items-center justify-between gap-2"
    >
      <span v-if="label" class="text-label-small text-n-slate-11 truncate">
        {{ label }}
      </span>
      <span
        v-if="showValue"
        class="text-label-small tabular-nums text-n-slate-11 shrink-0"
      >
        {{ valueText }}
      </span>
    </div>
    <div
      role="progressbar"
      :aria-label="label || undefined"
      :aria-valuenow="indeterminate ? undefined : value"
      :aria-valuemin="indeterminate ? undefined : 0"
      :aria-valuemax="indeterminate ? undefined : max"
      :aria-valuetext="valueText"
      :aria-busy="indeterminate ? 'true' : undefined"
      class="w-full overflow-hidden rounded-full bg-n-alpha-2"
      :class="SIZE_CLASSES[size]"
    >
      <div
        v-if="indeterminate"
        class="w-full h-full rounded-full animate-pulse motion-reduce:animate-none"
        :class="COLOR_CLASSES[color]"
      />
      <div
        v-else
        class="h-full rounded-full transition-all duration-500 motion-reduce:transition-none"
        :class="COLOR_CLASSES[color]"
        :style="{ width: `${percent}%` }"
      />
    </div>
    <span v-if="!showValue" class="sr-only">{{ valueText }}</span>
  </div>
</template>
